import React from 'react';
import {
  Button,
  Col,
  Input,
  Modal,
  ModalProps,
  Row,
  Space,
} from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined, CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { CommonScanToShipDialogBody } from './styles';
import { CopySpan, DataGrid, HoverBgButton, InfoButton, Label, Loading, ScreenMask, message } from 'components/common';
import { LOADING_ICON_SIZE1 } from 'constants/config';
import { deleteShipmentLineSerialNumber, fetchShipmentLineSerialNumber, verifyShipmentLineSerialNumber } from 'services/shipment';
import { playSound } from 'utils';
import CodeListInputDialog from './CodeListInputDialog';

type Props = ModalProps & {
  forwardAction?: Function;
  isShipmentVerified?: Function;
  lines: StringKAnyVPair[];
  onValidate?: Function;
};

const SerialNumberValidator = (props: Props) => {
  const { useState } = React;
  const [codeListInputDialogVisible, setCodeListInputDialogVisible] = useState(false);
  const [comAttrs, setComAttrs] = useState<any>();
  const [currentLine, setCurrentLine] = useState<StringKAnyVPair>({});
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [inited, setInited] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [serialNumberInputVersion, setSerialNumberInputVersion] = useState(0);
  const [snList, setSnList] = useState<StringKAnyVPair[]>([]);
  const snInputRef = React.useRef<any>(null);

  const columns = [
    {
      header: 'Serial Number',
      name: 'serialNumber',
      render(row: any) {
        const { data } = row;

        return <CopySpan inGrid value={data.serialNumber} />
      },
    },
    {
      header: '',
      name: '',
      sortable: false,
      textAlign: 'center',
      maxWidth: 120,
      render(row: any) {
        const { data } = row;
        return (
          <HoverBgButton
            hovertype="danger"
            onClick={() => onDeleteSerialNumber(data)}
          >
            <DeleteOutlined />
            Delete
          </HoverBgButton>
        );
      },
    },
  ];

  // eslint-disable-next-line
  const addSerialNumber = (sn: StringKAnyVPair) => {
    setSnList([ sn, ...snList ]);
  };

  const addSerialNumberList = (sns: StringKAnyVPair[]) => {
    setSnList([ ...sns, ...snList ]);
  };

  const clearSerialNumber = () => {
    setSerialNumber('');
    setSerialNumberInputVersion(serialNumberInputVersion + 1);
  };

  const closeCodeListInputDialog = () => {
    setCodeListInputDialogVisible(false);
  };

  const deleteSerialNumber = async (sn: StringKAnyVPair) => {
    setLockScreen(true);
    try {
      const res = await deleteShipmentLineSerialNumber(currentLine.shipmentNum, currentLine.shipmentLineNum, { 'SerialNumber': sn.serialNumber });

      if (res) {
        const idx = snList.findIndex(e => e.shipmentLineSerialNumberNum === sn.shipmentLineSerialNumberNum);

        if (idx > -1) {
          snList.splice(idx, 1);
          setSnList([...snList]);

          if (snList.length !== currentLine.verifyQty && snList.length <= currentLine.shippingQty) {
            //currentLine.verifyQty = currentLine.verifyQty - 1;
            currentLine.verifyQty = snList.length;
            setCurrentLine({ ...currentLine });

            if (props.onValidate) props.onValidate(currentLine);
          }
        }
      }
    } catch(e) {
      message.error({ content: `Deleted serial number error: ${e}` });
    } finally {
      setLockScreen(false);
    }
  };

  const dialogFooter = () => {
    //console.log('p', props);
    return (
      <Row align="middle" justify="space-between">
        {props.forwardAction && (
          <Button
            onClick={onClose}
            size="large"
          >
            <CloseOutlined />
            Close
          </Button>
        )}
        {props.lines.length < 2 && !props.forwardAction && <span>&nbsp;</span>}
        {props.lines.length > 1 && (<Space>
          <Button
            disabled={currentLineIndex === 0}
            onClick={() => onSwitchLine(currentLineIndex - 1)}
            size="large"
          >
            <ArrowLeftOutlined />
            Prev
          </Button>
          <Button
            disabled={currentLineIndex === props.lines.length - 1}
            onClick={() => onSwitchLine(currentLineIndex + 1)}
            size="large"
          >
            Next
            <ArrowRightOutlined />
          </Button>
        </Space>)}
        <Space>
          {!props.forwardAction && (
            <Button
              onClick={onClose}
              size="large"
            >
              <CloseOutlined />
              Close
            </Button>
          )}
          {props.forwardAction && <Button
            disabled={!isForwardable()}
            onClick={onForward}
            size="large"
            type="primary"
          >
            <CheckOutlined />
            Confirm
          </Button>}
        </Space>
      </Row>
    );
  };

  const focusSerialNumberInput = () => {
    snInputRef.current?.focus();
  };

  const getSnGridColumnsByLine = (line: StringKAnyVPair) => {
    const cols = [ ...columns ];

    if (isShipmentVerified(line)) {
      cols.splice(1, 1);
    }

    return cols;
  };

  const getSnGridData = (list: StringKAnyVPair[]) => {
    return list.sort((a, b) => b.shipmentLineSerialNumberNum - a.shipmentLineSerialNumberNum);
  };

  // eslint-disable-next-line
  const initialEnv = () => {
    const attrs = {
      ...props,
      lines: undefined,
    };

    setComAttrs(attrs);

    if (props.lines.length > 0) {
      setCurrentLine({ ...props.lines[currentLineIndex] });
    }
  };

  const isForwardable = () => {
    return props.lines.every(line => line.verifyQty === line.shippingQty);
  };

  const isSerialNumberInputUsable = () => {
    return currentLine.verifyQty < currentLine.shippingQty;
  };

  const isShipmentVerified = (line: StringKAnyVPair) => {
    let ret = false;

    if (props.isShipmentVerified) {
      ret = props.isShipmentVerified(line.shipmentNum);
    }

    return ret;
  };

  const isValidateButtonUsable = () => {
    return isSerialNumberInputUsable() && serialNumber.trim().length > 0;
  };

  // eslint-disable-next-line
  const loadLineSerialNumberList = async (
    line: StringKAnyVPair,
    updateCurrentLine = false,
  ) => {
    //if (props.lines.length === 0) return;
    //const line = props.lines[0];
    setLockScreen(true);
    try {
      const res = await fetchShipmentLineSerialNumber(line.shipmentNum, line.shipmentLineNum);
      //const res = await fetchShipmentLineSerialNumber(10000324235, 411479);

      if (Array.isArray(res)) {
        setSnList(res);

        if (res.length <= line.shippingQty && line.verifyQty !== res.length) {
          const lc = { ...line };

          lc.verifyQty = res.length;

          if (updateCurrentLine) setCurrentLine(lc);
          if (props.onValidate) props.onValidate(lc);
        }
      }

      if (line.verifyQty < line.shippingQty) {
        focusSerialNumberInput();
      }
    } catch(e) {
      message.error({ content: `Loaded serial number error: ${e}` });
    } finally {
      setLockScreen(false);
    }
  };

  const onDeleteSerialNumber = (row: any) => {
    Modal.confirm({
      centered: true,
      content: `Are you sure to delete the serial number [${row.serialNumber}]`,
      title: 'Confirm to delete',
      onOk() {
        deleteSerialNumber(row);
      },
    });
  };

  const onInputSerialNumber = (evt: any) => {
    const sn = evt.target.value.trim();

    //setSerialNumber(sn);
    if ([9, 13].indexOf(evt.keyCode) > -1) {
      verifySerialNumber(sn);
    }
  };

  const onClose = () => {
    // @ts-ignore
    if (props.onCancel) props.onCancel();
  };

  const onChangeSerialNumber = (evt: any) => {
    const sn = evt.target.value.trim();
    //console.log('ch', sn);
    setSerialNumber(sn);
  };

  const onForward = () => {
    setTimeout(() => {
      if (typeof props.forwardAction === 'function') props.forwardAction();
    }, 0);
    onClose();
  };

  const onSwitchLine = (idx: number) => {
    if (idx === currentLineIndex) return;

    const line = props.lines[idx];

    if (line) {
      setCurrentLine({...line});
      setCurrentLineIndex(idx);
      setSnList([]);
      loadLineSerialNumberList(line, true);
    }
  };

  const openCodeListInputDialog = () => {
    setCodeListInputDialogVisible(true);
  };

  const selectSerialNumberInput = () => {
    snInputRef.current?.select();
  };

  const validateSNButton = () => {
    return (
      <Button
        className="fs-24 ada-btn"
        disabled={!isValidateButtonUsable()}
        onClick={() => verifySerialNumber()}
        type="primary"
      >
        <CheckCircleOutlined />
      </Button>
    );
  };

  const verifySerialNumber = async (sn = serialNumber) => {
    if (currentLine.verifyQty >= currentLine.shippingQty) return;

    setLockScreen(true);
    playSound('bell');
    try {
      const res = await verifyShipmentLineSerialNumber(currentLine.shipmentNum, currentLine.shipmentLineNum, { 'SerialNumber': serialNumber });

      //console.log('res', res);
      //if (res && typeof res === 'object' && res.shipmentLineSerialNumberNum > 0) {
      if (res && typeof res === 'object' && Array.isArray(res)) {
        currentLine.verifyQty = currentLine.verifyQty + 1;
        setCurrentLine({ ...currentLine });
        //addSerialNumber(res);
        addSerialNumberList(res);
        clearSerialNumber();

        if (props.onValidate) props.onValidate(currentLine);

        if (currentLine.verifyQty < currentLine.shippingQty) {
          focusSerialNumberInput();
        }
      }
    } catch(e) {
      playSound('error');
      message.error({ content: `Verified serial number error: ${e}` });
      selectSerialNumberInput();
    } finally {
      setLockScreen(false);
    }
  };

  const verifySerialNumberList = async (sns: string[]) => {
    //console.log('sn', sns);
    if (currentLine.verifyQty >= currentLine.shippingQty) return;

    setLockScreen(true);
    try {
      const res = await verifyShipmentLineSerialNumber(currentLine.shipmentNum, currentLine.shipmentLineNum, { 'SerialNumbers': sns });

      //console.log('res', res);
      //if (res && typeof res === 'object' && res.shipmentLineSerialNumberNum > 0) {
      if (Array.isArray(res)) {
        //currentLine.verifyQty = currentLine.verifyQty + 1;
        currentLine.verifyQty = currentLine.verifyQty + res.length;
        setCurrentLine({ ...currentLine });
        addSerialNumberList(res);
        clearSerialNumber();

        if (props.onValidate) props.onValidate(currentLine);

        if (currentLine.verifyQty < currentLine.shippingQty) {
          focusSerialNumberInput();
        }
      }
    } catch(e) {
      playSound('error');
      message.error({ content: `Verified serial number error: ${e}` });
      selectSerialNumberInput();
    } finally {
      setLockScreen(false);
    }
  };

  React.useEffect(() => {
    if (!inited) {
      if (props.lines.length > 0) loadLineSerialNumberList(props.lines[currentLineIndex], true);

      initialEnv();
      setInited(true);
    }
  }, [currentLineIndex, inited, initialEnv, loadLineSerialNumberList, props]);

  return (<>
    <Modal
      {...comAttrs}
      bodyStyle={{ padding: 12 }}
      centered
      footer={dialogFooter()}
    >
      <CommonScanToShipDialogBody>
        <Row align="middle" className="mb-5">
          <Col span={10}>
            <Label className="label-bold tail-colon1 label-fs1_2">SKU</Label>
            <Label className="label-grey label-fs1_2">{currentLine.sku}</Label>
          </Col>
          <Col span={10}>
            {currentLine.upc && (<>
              <Label className="label-bold tail-colon1 label-fs1_2">UPC</Label>
              <Label className="label-grey label-fs1_2">{currentLine.upc}</Label>
            </>)}
          </Col>
          <Col className="align-right" span={4}>
            <Label className="label-bold label-fs2">
              {currentLine.verifyQty}/{currentLine.shippingQty}
            </Label>
          </Col>
        </Row>
        {!isShipmentVerified(currentLine) && (
          <Row className="mb-15" justify="space-between">
            <Input
              addonAfter={validateSNButton()}
              allowClear
              className="fs-24 h-50"
              disabled={!isSerialNumberInputUsable()}
              key={`sn-input-${serialNumberInputVersion}`}
              onChange={onChangeSerialNumber}
              onKeyUp={onInputSerialNumber}
              placeholder="Input serial number here"
              ref={snInputRef}
              style={{ width: 'calc(100% - 60px)' }}
            />
            <InfoButton
              className="fs-24 h-50 p-6-12"
              onClick={openCodeListInputDialog}
            >
              <i className="bi bi-list-check"></i>
            </InfoButton>
          </Row>
        )}
        <div>
          <DataGrid
            //columns={columns}
            columns={getSnGridColumnsByLine(currentLine)}
            count={snList.length}
            dataSource={getSnGridData(snList)}
            //limit={5}
            //onLimitChange={onLimitChange}
            //onSkipChange={onSkipChange}
            //pageSizes={[5, 20, 50, 100, 200]}
            pagination={false}
            rowKey="shipmentLineSerialNumberNum"
            style={{height: 'calc(100vh - 320px)', maxHeight: 600}}
          />
        </div>
      </CommonScanToShipDialogBody>
    </Modal>
    {codeListInputDialogVisible && (
      <CodeListInputDialog
        centered
        onCancel={closeCodeListInputDialog}
        onInputList={verifySerialNumberList}
        title="Input Serial Number"
        visible={codeListInputDialogVisible}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default SerialNumberValidator;
